import type {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import type {SubscriptionCheckoutRequest} from '@Components/checkout-flow/checkout-flow.types';

export const PREMIUM_PRICING_PANEL_ID = 'PremiumPricingPanel';

export const SUBSCRIPTION_PROMOTION_CODE_COOKIE = 'pmwspc';

/** Mirrors `SubscriptionPromotionVO::DISCOUNT_TYPE_PERCENT`; an AMOUNT promotion is a currency value. */
export const PROMO_DISCOUNT_TYPE_PERCENTAGE = 'PERCENTAGE';

/** The pricing modal only ever sells a single seat; team seat counts are chosen on the pricing page. */
export const SINGLE_SEAT_QUANTITY = 1;

/** Mirrors `SubscriptionVO::NUM_PURCHASELESS_DOWNLOADS`, the per-member download cap on a trial team. */
export const TRIAL_DOWNLOADS_INCLUDED = 10;

export const PKR_CURRENCY_CODE = 'PKR';

/** Mirrors `SubscriptionVO::NUM_FREE_CREDITS_PER_MONTH_FOR_PLAN_*`; plans billed in PKR get their own, lower allotment. */
export const PREMIUM_MONTHLY_CREDITS = 100;
export const PREMIUM_PLUS_MONTHLY_CREDITS = 250;
export const PREMIUM_MONTHLY_CREDITS_PKR = 40;
export const PREMIUM_PLUS_MONTHLY_CREDITS_PKR = 80;

/** A promotion with `months === 0` never stops applying, so the plan's own billing cycle text stands. */
export const PROMO_MONTHS_FOREVER = 0;

/** Mirrors `SubscriptionPromotionVO::MONTHS_ONE_TIME` — discounts the first invoice only. */
export const PROMO_MONTHS_ONE_TIME = -1;

/** A promotion covering a single billing cycle, whatever length that cycle is. */
export const PROMO_MONTHS_SINGLE_CYCLE = 1;

export interface PremiumPricingPanelProps {
  modalId: string;
  onGoBack?: () => void;
  isTrial?: boolean;
  upsellFeatureName?: string;
  promoCode?: string;
  /** What to record a trial started from this modal against. Empty falls back to DEFAULT_TRIAL_SOURCE. */
  trialSource?: string;
}

export interface PremiumPricingPanelState {
  plans: Plan[];
  isWaitingForAJAXResponse: boolean;
  selectedCycle: PlanCycle;
  yotpoData: YotpoReviewsMeta | undefined;
  appliedPromoCode: AppliedPromoCode | null;
  isPromoCodeLookupPending: boolean;
  checkoutRequest: SubscriptionCheckoutRequest | null;
  localCheckoutCurrencyCode: string | null;
}

export interface PromoEligiblePlans {
  levels: number[];
  months: number[];
}

export interface AppliedPromoCode {
  code: string;
  discountFraction: number;
  months: number;
  eligiblePlans: PromoEligiblePlans;
}

export interface YotpoReviewsMeta {
  idYotpoProduct: number;
  reviewCount: number;
  averageStarRating: number;
}

export interface PlanCardProps {
  plan: Plan;
  isTrial: boolean;
  onCloseModal: () => void;
  isHighlighted?: boolean;
  isCurrentPlan?: boolean;
  isLowerTierThanUserPlan?: boolean;
  containerSpacingClass?: string;
  upsellFeatureName?: string;
  trialSource?: string;
}

export interface Plan {
  priceId: number | string;
  name: string;
  description: string;
  price: number;
  currencyCode: string;
  level: USER_PREMIUM_LEVELS;
  features: PlanFeature[];
  cycle: PlanCycle;
}

export interface PlanFeature {
  featureName: string;
  icon: string;
  description: string;
  isComingSoon?: boolean;
  tooltip?: string;
  isHighlighted?: boolean;
  centerAlign?: boolean;
}

export enum PlanCycle {
  MONTHLY = 1,
  YEARLY = 12,
}
